/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useFavoritePrayerAction } from "@packages/kids";
import type { Images, Prayer } from "@packages/sdk";
import type { DropdownMenuProps } from "@packages/ui";
import {
  AddToQueue,
  ContentMoreMenuHeader,
  DropdownMenu,
  DropdownMenuItem,
  IconButton,
  MoreIcon,
  spacing,
  StarIcon,
} from "@packages/ui";
import type { MouseEvent } from "react";

const dropdownMenuContentStyle: Interpolation = { paddingTop: spacing.l };

type PrayerMoreMenuProps = DropdownMenuProps & {
  imageSrc?: Images;
  prayer: Pick<
    Prayer,
    | "id"
    | "is_favorite"
    | "title"
    | "label_desc"
    | "lengths"
    | "has_access"
    | "has_video"
  >;
};

export const PrayerMoreMenu = ({
  prayer,
  imageSrc,
  ...props
}: PrayerMoreMenuProps) => {
  const updateFavorite = useFavoritePrayerAction({
    id: prayer.id,
    has_access: prayer.has_access,
    title: prayer.title,
    images: imageSrc,
  });
  const t = useTranslations();

  const onClickFavorite = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    updateFavorite();
  };

  return (
    <DropdownMenu
      align="end"
      contentCSS={dropdownMenuContentStyle}
      modal={false}
      trigger={
        <IconButton
          icon={<MoreIcon />}
          onClick={(e) => {
            e.preventDefault();
          }}
          variant="transparentPrimary"
        />
      }
      {...props}
    >
      <ContentMoreMenuHeader
        imageSrc={imageSrc}
        metadata={prayer.lengths}
        subtitle={prayer.label_desc}
        title={prayer.title}
        hasVideoContent={prayer.has_video}
      />
      <DropdownMenuItem
        onClick={onClickFavorite}
        startIcon={prayer.is_favorite ? <StarIcon.Off /> : <StarIcon.Outline />}
      >
        {prayer.is_favorite
          ? t("dialog_prayer_options_unfavorite")
          : t("dialog_prayer_options_favorite")}
      </DropdownMenuItem>
      <AddToQueue id={prayer.id} type={"prayer"} />
    </DropdownMenu>
  );
};
